/**
 * OS-1710 — sesión de la consola de plataforma: almacenamiento, expiración y cierre.
 * Todo acceso a las claves de `sessionStorage` de la sesión pasa por aquí.
 */

export const SESSION_KEYS = {
  accessToken: 'platform_token',
  partialToken: 'platform_partial_token',
  email: 'platform_email',
  lastActivity: 'platform_last_activity',
} as const;

/** Cierre por inactividad: 30 minutos sin interacción. */
export const IDLE_TIMEOUT_MS = 30 * 60 * 1000;

export type SessionEndReason = 'expired' | 'idle' | 'logout';

/** Query string que `/login` interpreta para explicar por qué terminó la sesión. */
export const LOGIN_REASON_PARAM: Record<Exclude<SessionEndReason, 'logout'>, string> = {
  expired: 'expired',
  idle: 'idle',
};

export function getAccessToken(): string | null {
  return sessionStorage.getItem(SESSION_KEYS.accessToken);
}

export function getPartialToken(): string | null {
  return sessionStorage.getItem(SESSION_KEYS.partialToken);
}

export function getSessionEmail(): string {
  return sessionStorage.getItem(SESSION_KEYS.email) ?? '';
}

export function startPendingMfa(partialToken: string, email: string): void {
  sessionStorage.setItem(SESSION_KEYS.partialToken, partialToken);
  sessionStorage.setItem(SESSION_KEYS.email, email);
}

/** Abre la sesión completa y descarta el token MFA parcial, si lo había. */
export function startSession(accessToken: string, email: string): void {
  sessionStorage.removeItem(SESSION_KEYS.partialToken);
  sessionStorage.setItem(SESSION_KEYS.accessToken, accessToken);
  if (email) {
    sessionStorage.setItem(SESSION_KEYS.email, email);
  }
  recordActivity();
}

export function clearSession(): void {
  for (const key of Object.values(SESSION_KEYS)) {
    sessionStorage.removeItem(key);
  }
}

export function recordActivity(now: number = Date.now()): void {
  sessionStorage.setItem(SESSION_KEYS.lastActivity, String(now));
}

export function getLastActivity(): number | null {
  const raw = sessionStorage.getItem(SESSION_KEYS.lastActivity);
  const value = raw === null ? Number.NaN : Number(raw);
  return Number.isFinite(value) ? value : null;
}

export function isIdle(now: number = Date.now(), timeoutMs: number = IDLE_TIMEOUT_MS): boolean {
  const last = getLastActivity();
  return last !== null && now - last >= timeoutMs;
}

/**
 * Lee `exp` (segundos) del payload sin verificar la firma: solo sirve para anticipar la
 * expiración en la UI. El backend sigue siendo quien valida el token.
 */
export function getTokenExpiryMs(token: string): number | null {
  const segment = token.split('.')[1];
  if (!segment) {
    return null;
  }
  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const payload = JSON.parse(atob(padded)) as { exp?: unknown };
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/** Un token ilegible o sin `exp` se trata como expirado. */
export function isTokenExpired(token: string, now: number = Date.now()): boolean {
  const expiry = getTokenExpiryMs(token);
  return expiry === null || expiry <= now;
}

export function loginPathFor(reason: SessionEndReason): string {
  return reason === 'logout' ? '/login' : `/login?${LOGIN_REASON_PARAM[reason]}=1`;
}

/**
 * Cierra la sesión y lleva al login con el motivo. Usa `location.replace` porque se invoca
 * también fuera de React (cliente HTTP) y no debe quedar en el historial.
 */
export function endSession(reason: SessionEndReason): void {
  clearSession();
  const target = loginPathFor(reason);
  if (`${window.location.pathname}${window.location.search}` !== target) {
    window.location.replace(target);
  }
}
